import { Fragment, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { MenuItem, Popper, Grow, Paper, MenuList, ClickAwayListener } from '@mui/material';
import { ExpandMore, Launch } from '@mui/icons-material';

const NavItemWithSubMenu = ({
    id,
    name,
    isParentClickable,
    parentUrlLink,
    children: subItems,
    classes,
}) => {
    const menuId = `${id}-menu`;
    const [childMenuOpen, setChildMenuOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setChildMenuOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setChildMenuOpen(false);
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setChildMenuOpen(false);
        } else if (event.key === 'Escape') {
            setChildMenuOpen(false);
        }
    };

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = useRef(childMenuOpen);
    useEffect(() => {
        if (prevOpen.current === true && childMenuOpen === false) {
            anchorRef.current?.focus();
        }

        prevOpen.current = childMenuOpen;
    }, [childMenuOpen]);

    return (
        <Fragment key={`frag-${id.toString()}`}>
            <MenuItem
                component={NavLink}
                className={`${classes.deskLink} ${classes.fontBoldHeader}`}
                to={{ pathname: parentUrlLink }}
                onClick={(event) => {
                    handleToggle();
                    // if isParentClickable is marked as false, we don't want to navigate to the page
                    // we need parentUrlLink to still be set on the parent to ensure the navlink is underlined
                    if (!isParentClickable) {
                        event.preventDefault();
                    }
                }}
                ref={anchorRef}
                aria-controls={childMenuOpen ? menuId : undefined}
                aria-expanded={childMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
            >
                {name}
                <ExpandMore className={classes.arrowIcon} fontSize="small" />
            </MenuItem>
            <Popper
                open={childMenuOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                className={classes.paperPopper}
                placement="bottom-start"
                modifiers={[{ enabled: true, name: 'offset', options: { offset: [0, 2] } }]}
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    autoFocusItem={childMenuOpen}
                                    id={menuId}
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {subItems.map((item) => {
                                        if (item.externalUrl) {
                                            return (
                                                <a
                                                    key={`a-${item.id.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={item.link}
                                                    className={classes.deskLink}
                                                    onClick={handleClose}
                                                >
                                                    <MenuItem
                                                        classes={{ root: classes.subNavMenuItem }}
                                                    >
                                                        {item.name}
                                                        <Launch
                                                            fontSize="small"
                                                            style={{ marginLeft: 'auto' }}
                                                        />
                                                    </MenuItem>
                                                </a>
                                            );
                                        }
                                        if (item.hasLtDotComBaseUrl) {
                                            return (
                                                <a
                                                    key={`a-${item.id.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={`${process.env.REACT_APP_LOGIN_URL}${item.link}`}
                                                    className={classes.deskLink}
                                                    onClick={handleClose}
                                                >
                                                    <MenuItem
                                                        classes={{ root: classes.subNavMenuItem }}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                </a>
                                            );
                                        }
                                        return (
                                            <NavLink
                                                key={`NavLink-${item.id.toString()}`}
                                                exact
                                                to={{ pathname: item.link }}
                                                className={classes.deskLink}
                                                onClick={handleClose}
                                            >
                                                <MenuItem
                                                    classes={{ root: classes.subNavMenuItem }}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            </NavLink>
                                        );
                                    })}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Fragment>
    );
};

export default NavItemWithSubMenu;
